/**
 * Environment Configuration - Single Source of Truth
 * 
 * This file contains ALL environment-specific configuration for the Harry application.
 * This is the ONLY place where environment information should be defined.
 * 
 * CRITICAL: All scripts, components, and services MUST import from this file
 * instead of hardcoding environment values.
 * 
 * @see Agents.md Section 22 for usage requirements
 */

/**
 * Environment Constants
 */
export const ENV_DEV = 'dev';
export const ENV_PROD = 'prod';

/**
 * Environment type
 */
export type Environment = typeof ENV_DEV | typeof ENV_PROD;

/**
 * Complete environment configuration
 */
export interface EnvironmentConfig {
  /** Firebase project ID */
  firebaseProject: string;
  /** Firestore database name */
  databaseName: string;
  /** Firebase Hosting URL */
  hostingUrl: string;
  /** Firebase AppHosting URL */
  appHostingUrl: string;
  /** GitHub repository URL */
  githubRepo: string;
  /** Display name for logging */
  displayName: string;
}

/** Meta Pixel id for `/self-assessment` ads reporting (public, not a secret). */
export const META_PIXEL_ID = '235566225075520';

export function getMetaPixelId(): string {
  const fromProcess =
    typeof process !== 'undefined' ? process.env.VITE_META_PIXEL_ID || process.env.META_PIXEL_ID : '';
  if (fromProcess?.trim()) return fromProcess.trim();
  return META_PIXEL_ID;
}

/**
 * Meta Conversions API (CAPI) access token — server-only, never exposed to client.
 * Set via META_CAPI_ACCESS_TOKEN (or PROD_META_CAPI_ACCESS_TOKEN / DEV_META_CAPI_ACCESS_TOKEN).
 * Returns empty string when unset; CAPI calls are silently skipped in that case.
 */
export function getMetaCapiAccessToken(): string {
  if (typeof process === 'undefined') return '';
  const envPrefix = process.env.NODE_ENV === 'production' ? 'PROD_' : 'DEV_';
  return (
    process.env[`${envPrefix}META_CAPI_ACCESS_TOKEN`] ||
    process.env.META_CAPI_ACCESS_TOKEN ||
    ''
  ).trim();
}

/**
 * Complete environment configurations for both dev and prod
 * 
 * This is the SINGLE SOURCE OF TRUTH for all environment information.
 * All scripts, components, and services must use these values.
 */
export const ENVIRONMENT_CONFIG: Record<Environment, EnvironmentConfig> = {
  dev: {
    firebaseProject: 'harry-playground-x8x90',
    databaseName: 'hdev',
    hostingUrl: 'https://harry-playground-x8x90.web.app',
    appHostingUrl: 'iowa--harry-playground-x8x90.us-central1.hosted.app',
    githubRepo: 'https://github.com/Essenpreis/harry-playground',
    displayName: 'Development (harry-playground-x8x90)',
  },
  prod: {
    firebaseProject: 'harry-y02hw',
    databaseName: 'hdev',
    hostingUrl: 'https://harry-y02hw.web.app',
    appHostingUrl: 'harry-prod-iowa--harry-y02hw.us-central1.hosted.app',
    githubRepo: 'https://github.com/Essenpreis/harry-prod',
    displayName: 'Production (harry-y02hw)',
  },
} as const;

/**
 * Get environment configuration
 * 
 * @param env - Environment type ('dev' or 'prod')
 * @returns Environment configuration object
 */
export function getEnvironmentConfig(env: Environment): EnvironmentConfig {
  return ENVIRONMENT_CONFIG[env];
}

/**
 * Firebase project id from Cloud Run / App Hosting / Admin SDK env.
 * App Hosting sets GOOGLE_CLOUD_PROJECT (and often FIREBASE_CONFIG) but not
 * always FIREBASE_PROJECT_ID — callers must not require the latter alone.
 */
export function getServerFirebaseProjectId(): string {
  if (typeof process === 'undefined' || !process.env) {
    return '';
  }
  const fromNamedEnv =
    process.env.FIREBASE_PROJECT_ID ||
    process.env.FIREBASE_PROJECT ||
    process.env.GCP_PROJECT ||
    process.env.GOOGLE_CLOUD_PROJECT ||
    process.env.GCLOUD_PROJECT ||
    '';
  if (fromNamedEnv) {
    return fromNamedEnv;
  }
  const rawConfig = process.env.FIREBASE_CONFIG;
  if (!rawConfig) {
    return '';
  }
  try {
    const parsed = JSON.parse(rawConfig) as { projectId?: string };
    return parsed.projectId || '';
  } catch {
    return '';
  }
}

export function environmentFromFirebaseProjectId(
  projectId: string | undefined | null
): Environment | null {
  const id = projectId?.trim() || '';
  if (id === ENVIRONMENT_CONFIG.dev.firebaseProject) {
    return ENV_DEV;
  }
  if (id === ENVIRONMENT_CONFIG.prod.firebaseProject) {
    return ENV_PROD;
  }
  return null;
}

/**
 * Map a request hostname to Harry environment (Hosting, App Hosting, preview).
 */
export function environmentFromHostname(hostname: string): Environment {
  if (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    isPrivateLanIPv4Hostname(hostname) ||
    hostname.includes(ENVIRONMENT_CONFIG.dev.firebaseProject)
  ) {
    return ENV_DEV;
  }
  if (hostname.includes(ENVIRONMENT_CONFIG.prod.firebaseProject)) {
    return ENV_PROD;
  }
  return ENV_PROD;
}

/**
 * Get current environment based on project id (server) or hostname (browser).
 *
 * @returns 'dev' or 'prod'
 */
export function getCurrentEnvironment(): Environment {
  if (typeof window === 'undefined') {
    return environmentFromFirebaseProjectId(getServerFirebaseProjectId()) ?? ENV_DEV;
  }

  return environmentFromHostname(window.location.hostname);
}

/**
 * Get current environment configuration (client-side only)
 * 
 * @returns Current environment configuration based on hostname
 */
export function getCurrentEnvironmentConfig(): EnvironmentConfig {
  const env = getCurrentEnvironment();
  return ENVIRONMENT_CONFIG[env];
}

/**
 * Get environment from command-line arguments (server-side/scripts)
 * 
 * Checks for --prod or --production flags in process.argv
 * 
 * @returns 'dev' or 'prod' based on --prod flag
 */
export function getEnvironmentFromArgs(): Environment {
  const args = process.argv.slice(2);
  return args.includes('--prod') || args.includes('--production') ? 'prod' : 'dev';
}

/**
 * Get Firebase project ID for an environment
 * 
 * @param env - Environment type ('dev' or 'prod')
 * @returns Firebase project ID
 */
export function getFirebaseProject(env: Environment): string {
  return ENVIRONMENT_CONFIG[env].firebaseProject;
}

/**
 * Default Cloud Storage bucket of the environment's Firebase project
 * (the same bucket as the web config's `storageBucket`).
 */
export function getStorageBucket(env: Environment): string {
  return `${ENVIRONMENT_CONFIG[env].firebaseProject}.firebasestorage.app`;
}

/**
 * Environment whose Storage hosts the help videos. There is one copy of every help
 * video, in the dev project's Storage; dev and prod both play from it (Michail,
 * 2026-10-16: the help-video maintainer has dev access only; ADR-0025).
 */
export const HELP_VIDEOS_ENV: Environment = ENV_DEV;

/**
 * Bucket that hosts the help videos, for every environment: the dev project's
 * default bucket. Use it for every help video URL and for every help video upload.
 */
export function getHelpVideosBucket(): string {
  return getStorageBucket(HELP_VIDEOS_ENV);
}

/**
 * Public HTTPS URL of a Storage object. Works without a download token when
 * `storage.rules` allows public read on the object's path.
 */
export function getStoragePublicUrl(bucket: string, objectPath: string): string {
  return `${STORAGE_PUBLIC_ORIGIN}/v0/b/${bucket}/o/${encodeURIComponent(objectPath)}?alt=media`;
}

/** Origin of public Storage object URLs (`getStoragePublicUrl`). */
export const STORAGE_PUBLIC_ORIGIN = 'https://firebasestorage.googleapis.com';

/**
 * CSP `media-src` source that lets the Help player load help videos and their caption
 * tracks: the help-videos bucket only, not every Storage bucket or every https: URL.
 */
export function getHelpVideosMediaSrc(): string {
  return `${STORAGE_PUBLIC_ORIGIN}/v0/b/${getHelpVideosBucket()}/o/`;
}

/**
 * Public URL of a help video or caption file, from its bucket-relative path
 * (`help-videos/<id>/<stamp>.mp4|.vtt`, as stored in the Video script frontmatter).
 */
export function getHelpVideoPublicUrl(objectPath: string): string {
  return getStoragePublicUrl(getHelpVideosBucket(), objectPath);
}

/** Port of the local dev server (`npm run dev`, server.js default). */
export const LOCAL_DEV_SERVER_PORT = 8080;

/**
 * Harry origins that play help videos from the help-videos bucket: the hosted apps plus
 * the local dev server. The bucket's CORS config must allow them, because the player
 * fetches the video and its caption track (VTT) cross-origin.
 */
export function getHelpVideosPlaybackOrigins(): string[] {
  const origins = new Set<string>();
  for (const cfg of Object.values(ENVIRONMENT_CONFIG)) {
    origins.add(cfg.hostingUrl);
    origins.add(`https://${cfg.firebaseProject}.firebaseapp.com`);
    origins.add(`https://${cfg.appHostingUrl}`);
  }
  origins.add(`http://${getLocalhostHostname()}:${LOCAL_DEV_SERVER_PORT}`);
  origins.add(`http://${getLocalhostIP()}:${LOCAL_DEV_SERVER_PORT}`);
  return [...origins];
}

/**
 * Env var that holds the Firebase CI service-account JSON for `env`.
 * Derived from the project id (`harry-y02hw` → `FIREBASE_SERVICE_ACCOUNT_HARRY_Y02HW`).
 */
export function getFirebaseServiceAccountEnvVar(env: Environment): string { // pragma: allowlist secret
  return `FIREBASE_SERVICE_ACCOUNT_${getFirebaseProject(env).toUpperCase().replace(/-/g, '_')}`;
}

/**
 * Get database name for an environment
 * 
 * @param env - Environment type ('dev' or 'prod')
 * @returns Database name
 */
export function getDatabaseName(env: Environment): string {
  return ENVIRONMENT_CONFIG[env].databaseName;
}

/**
 * Get hosting URL for an environment
 * 
 * @param env - Environment type ('dev' or 'prod')
 * @returns Hosting URL
 */
export function getHostingUrl(env: Environment): string {
  return ENVIRONMENT_CONFIG[env].hostingUrl;
}

/**
 * Get AppHosting URL for an environment
 * 
 * @param env - Environment type ('dev' or 'prod')
 * @returns AppHosting URL
 */
export function getAppHostingUrl(env: Environment): string {
  return ENVIRONMENT_CONFIG[env].appHostingUrl;
}

/**
 * Get GitHub repository URL for an environment
 * 
 * @param env - Environment type ('dev' or 'prod')
 * @returns GitHub repository URL
 */
export function getGitHubRepo(env: Environment): string {
  return ENVIRONMENT_CONFIG[env].githubRepo;
}

/**
 * Get GitHub repository owner for an environment
 * 
 * @param env - Environment type ('dev' or 'prod')
 * @returns GitHub repository owner (e.g., 'Essenpreis')
 */
export function getGitHubRepoOwner(env: Environment): string {
  const repoUrl = ENVIRONMENT_CONFIG[env].githubRepo;
  // Extract owner from URL like 'https://github.com/Essenpreis/harry-playground'
  const match = repoUrl.match(/github\.com\/([^\/]+)\//);
  return match ? match[1] : 'Essenpreis'; // Default to Essenpreis if parsing fails
}

/**
 * Get GitHub repository name for an environment
 * 
 * @param env - Environment type ('dev' or 'prod')
 * @returns GitHub repository name extracted from the configured GitHub repository URL
 */
export function getGitHubRepoName(env: Environment): string {
  const repoUrl = ENVIRONMENT_CONFIG[env].githubRepo;
  // Extract repo name from URL like 'https://github.com/Essenpreis/harry-playground'
  const match = repoUrl.match(/github\.com\/[^\/]+\/([^\/]+)/);
  if (match) {
    return match[1];
  }
  // Fallback: Extract from URL path if regex fails
  try {
    const url = new URL(repoUrl);
    const pathParts = url.pathname.split('/').filter(Boolean);
    if (pathParts.length >= 2) {
      return pathParts[pathParts.length - 1];
    }
  } catch {
    // URL parsing failed
  }
  // Last resort: Derive from project ID structure (remove suffixes)
  const projectId = ENVIRONMENT_CONFIG[env].firebaseProject;
  return projectId.replace(/-x8x90$/, '').replace(/-y02hw$/, '');
}

/**
 * Get GitHub repository in owner/repo format for an environment
 * 
 * @param env - Environment type ('dev' or 'prod')
 * @returns GitHub repository in owner/repo format (e.g., 'Essenpreis/harry-playground')
 */
export function getGitHubRepoOwnerName(env: Environment): string {
  const owner = getGitHubRepoOwner(env);
  const repoName = getGitHubRepoName(env);
  return `${owner}/${repoName}`;
}

/**
 * Check if an environment is production
 * 
 * @param env - Environment type ('dev' or 'prod')
 * @returns true if production, false otherwise
 */
export function isProduction(env: Environment): boolean {
  return env === 'prod';
}

/**
 * Check if an environment is development
 * 
 * @param env - Environment type ('dev' or 'prod')
 * @returns true if development, false otherwise
 */
export function isDevelopment(env: Environment): boolean {
  return env === 'dev';
}

/**
 * Get preview channel host pattern for an environment
 * 
 * @param env - Environment type ('dev' or 'prod')
 * @returns Regex pattern string for preview channel hosts
 */
export function getPreviewChannelHostPattern(env: Environment): string {
  const projectId = ENVIRONMENT_CONFIG[env].firebaseProject;
  return `^${projectId}--[a-z0-9-]+\\.web\\.app$`;
}

/**
 * True when `hostname` is a Firebase Hosting PR preview channel for this app
 * (`{projectId}--{channel}.web.app`). Stable `{projectId}.web.app` hosts are not
 * preview channels.
 */
export function isPreviewChannelHostname(hostname: string): boolean {
  return (['dev', 'prod'] as const).some((env) =>
    new RegExp(getPreviewChannelHostPattern(env)).test(hostname)
  );
}

/**
 * Get localhost hostname (constant)
 * 
 * @returns 'localhost'
 */
export function getLocalhostHostname(): string {
  return 'localhost';
}

/**
 * Get localhost IPv4 address (constant)
 * 
 * @returns '127.0.0.1'
 */
export function getLocalhostIP(): string {
  return '127.0.0.1';
}

/**
 * Get localhost IPv6 address (constant)
 * 
 * @returns '::1'
 */
export function getLocalhostIPv6(): string {
  return '::1';
}

/**
 * True when `hostname` is an IPv4 address on a typical local network (RFC1918 or IPv4 link-local).
 * Used so devices hitting the dev server via LAN IP use the playground dev Firebase project,
 * matching behavior for localhost.
 */
export function isPrivateLanIPv4Hostname(hostname: string): boolean {
  const parts = hostname.split('.');
  if (parts.length !== 4) {
    return false;
  }
  const octets = parts.map((p) => Number.parseInt(p, 10));
  if (octets.some((n) => !Number.isInteger(n) || n < 0 || n > 255)) {
    return false;
  }
  const [a, b] = octets;
  if (a === 10) {
    return true;
  }
  if (a === 172 && b >= 16 && b <= 31) {
    return true;
  }
  if (a === 192 && b === 168) {
    return true;
  }
  if (a === 169 && b === 254) {
    return true;
  }
  return false;
}

// Export default for convenience
export default {
  ENVIRONMENT_CONFIG,
  getEnvironmentConfig,
  getCurrentEnvironment,
  getServerFirebaseProjectId,
  environmentFromFirebaseProjectId,
  environmentFromHostname,
  getCurrentEnvironmentConfig,
  getEnvironmentFromArgs,
  getFirebaseProject,
  getFirebaseServiceAccountEnvVar,
  getDatabaseName,
  getHostingUrl,
  getAppHostingUrl,
  getGitHubRepo,
  getGitHubRepoOwner,
  getGitHubRepoName,
  getGitHubRepoOwnerName,
  isProduction,
  isDevelopment,
  getPreviewChannelHostPattern,
  isPreviewChannelHostname,
  getLocalhostHostname,
  getLocalhostIP,
  getLocalhostIPv6,
  isPrivateLanIPv4Hostname,
  META_PIXEL_ID,
  getMetaPixelId,
};
